import React, {useState, useMemo} from "react"
import {IconPlug} from "@tabler/icons-react"
import {IntegrationCard} from "./IntegrationCard"
import {IntegrationInfoBoxes} from "./IntegrationInfoBoxes"
import {CapacityLimitModal} from "./CapacityLimitModal"
import {ManageIntegrationModal} from "./ManageIntegrationModal"
import {INTEGRATIONS_DATA} from "../data/integrations"
import {IntegrationType, Integration} from "../types"

interface IntegrationTabProps {
  title?: string
  description?: string
  icon?: React.ComponentType<any>
  integrationTypes: IntegrationType[]
  themeColor?: "blue" | "purple"
  showInfoBoxes?: boolean
  context?: "archiving" | "auth"
}

export function IntegrationTab({
  title = "Integrations",
  description = "Manage authentication, user import, and data source integrations",
  icon: Icon = IconPlug,
  integrationTypes,
  themeColor = "blue",
  showInfoBoxes = false,
  context = "archiving",
}: IntegrationTabProps) {
  const [integrations, setIntegrations] = useState(INTEGRATIONS_DATA)
  const [activeFilter, setActiveFilter] = useState<string>("All")
  const [capacityModalOpen, setCapacityModalOpen] = useState(false)
  const [manageModalOpen, setManageModalOpen] = useState(false)
  const [selectedIntegration, setSelectedIntegration] =
    useState<Integration | null>(null)

  // Capacity limits per tab context
  const capacityLimit = 1 // 1 integration per tab (auth or archiving)

  const handleToggleIntegration = (id: string) => {
    const integration = integrations.find((i) => i.id === id)
    if (!integration) return

    // If trying to connect (not enabled) and not coming soon, show capacity modal
    if (!integration.enabled && !integration.comingSoon) {
      // Check if this integration belongs to the current tab's types
      const belongsToTab = integration.types.some((type) =>
        integrationTypes.includes(type)
      )
      if (belongsToTab) {
        setCapacityModalOpen(true)
        return
      }
    }

    // If we get here, it's either a manage action or coming soon
    console.log(`Toggle integration: ${id}`)
    // In a real implementation, this would make an API call
  }

  const handleManageIntegration = (integration: Integration) => {
    setSelectedIntegration(integration)
    setManageModalOpen(true)
  }

  const handleDisableIntegration = (id: string) => {
    setIntegrations((prev) =>
      prev.map((integration) =>
        integration.id === id ? {...integration, enabled: false} : integration
      )
    )
    console.log(`Disabled integration: ${id}`)
  }

  // Filter to only show integrations of the specified types
  const relevantIntegrations = useMemo(() => {
    const filtered = integrations.filter((integration) =>
      integration.types.some((type) => integrationTypes.includes(type))
    )
    console.log(
      "🎨 Relevant integrations for types",
      integrationTypes,
      ":",
      filtered
    )
    return filtered
  }, [integrations, integrationTypes])

  const filteredIntegrations = useMemo(() => {
    if (activeFilter === "All") {
      return relevantIntegrations
    }
    if (activeFilter === "Connected") {
      return relevantIntegrations.filter((integration) => integration.enabled)
    }

    // Map simplified filter names to integration types
    const filterTypeMap: Record<string, IntegrationType[]> = {
      "Auth / SSO": ["Authentication"],
      "HR / User Import": ["HR & Directory"],
      "Data Sources": ["Messaging", "Storage", "Call Center"],
    }

    const targetTypes = filterTypeMap[activeFilter]
    if (targetTypes) {
      return relevantIntegrations.filter((integration) =>
        integration.types.some((type) => targetTypes.includes(type))
      )
    }

    // Fallback to original type filtering for backward compatibility
    return relevantIntegrations.filter((integration) =>
      integration.types.includes(activeFilter as IntegrationType)
    )
  }, [relevantIntegrations, activeFilter])

  const enabledCount = relevantIntegrations.filter(
    (integration) => integration.enabled
  ).length
  const totalCount = relevantIntegrations.length

  const filters = [
    "All",
    "Connected",
    "Auth / SSO",
    "HR / User Import",
    "Data Sources",
  ]

  // Helper function to get count for simplified filters
  const getFilterCount = (filter: string): number => {
    if (filter === "Connected") return enabledCount

    const filterTypeMap: Record<string, IntegrationType[]> = {
      "Auth / SSO": ["Authentication"],
      "HR / User Import": ["HR & Directory"],
      "Data Sources": ["Messaging", "Storage", "Call Center"],
    }

    const targetTypes = filterTypeMap[filter]
    if (targetTypes) {
      return relevantIntegrations.filter((integration) =>
        integration.types.some((type) => targetTypes.includes(type))
      ).length
    }

    return 0
  }

  // Theme colors
  const themeColors = {
    blue: {
      iconBg: "bg-blue-50",
      iconColor: "text-blue-600",
      activeBg: "bg-blue-100",
      activeText: "text-blue-700",
    },
    purple: {
      iconBg: "bg-purple-50",
      iconColor: "text-purple-600",
      activeBg: "bg-purple-100",
      activeText: "text-purple-700",
    },
  }

  const colors = themeColors[themeColor]

  return (
    <>
      <div className="flex w-full flex-col overflow-auto rounded-lg border border-black/5 bg-white shadow-sm">
        {/* Header */}
        <div className="flex-shrink-0 border-b border-gray-100 p-6">
          <div className="flex items-center gap-3">
            <div
              className={`flex h-10 w-10 items-center justify-center rounded-lg ${colors.iconBg}`}
            >
              <Icon size={20} className={colors.iconColor} />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
              <p className="text-sm text-gray-500">
                {description} - {enabledCount} of {totalCount} integrations
                connected
              </p>
            </div>
          </div>

          {/* Info boxes */}
          {showInfoBoxes && (
            <div className="mt-6">
              <IntegrationInfoBoxes types={integrationTypes} />
            </div>
          )}

          {/* Filter buttons */}
          <div className="mt-6 flex gap-2">
            {filters.map((filter) => (
              <button
                key={filter}
                onClick={() => setActiveFilter(filter)}
                className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  activeFilter === filter
                    ? `${colors.activeBg} ${colors.activeText}`
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {filter}
                {filter !== "All" && (
                  <span className="ml-1 text-xs">
                    ({getFilterCount(filter)})
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
            {filteredIntegrations.map((integration) => (
              <IntegrationCard
                key={integration.id}
                integration={integration}
                onToggle={handleToggleIntegration}
                onManage={handleManageIntegration}
              />
            ))}
          </div>

          {/* Empty state if no integrations */}
          {filteredIntegrations.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="mb-4 flex-shrink-0 rounded-lg bg-gray-50 p-4 text-gray-400">
                <Icon size={48} />
              </div>
              <h3 className="mb-2 text-lg font-semibold text-gray-900">
                No integrations found
              </h3>
              <p className="max-w-md text-gray-500">
                No integrations match the current filter. Try selecting a
                different category.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Capacity Limit Modal */}
      <CapacityLimitModal
        isOpen={capacityModalOpen}
        onClose={() => setCapacityModalOpen(false)}
        currentCount={
          relevantIntegrations.filter(
            (i) =>
              i.enabled &&
              i.types.some((type) => integrationTypes.includes(type))
          ).length
        }
        maxCount={capacityLimit}
        context={context}
      />

      {/* Manage Integration Modal */}
      {selectedIntegration && (
        <ManageIntegrationModal
          isOpen={manageModalOpen}
          onClose={() => {
            setManageModalOpen(false)
            setSelectedIntegration(null)
          }}
          integration={selectedIntegration}
          onDisable={handleDisableIntegration}
        />
      )}
    </>
  )
}
